import { useState, useCallback } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';

interface ReportData {
  kycByMonth: Array<{ month: string; submitted: number; approved: number; rejected: number }>;
  customersByRisk: Array<{ level: string; count: number }>;
  topDocumentTypes: Array<{ type: string; count: number }>;
  avgProcessingTime: number;
}

export default function Reports() {
  const [dateRange, setDateRange] = useState('30d');
  const { data: report, loading } = useApiQuery<ReportData>({ endpoint: `/reports/summary?range=${dateRange}` });

  const maxSubmitted = Math.max(...(report?.kycByMonth.map((m) => m.submitted) || [0]), 1);

  const exportCsv = useCallback(() => {
    if (!report) return;
    const rows: string[] = [];
    rows.push('KYC Trends');
    rows.push('Month,Submitted,Approved,Rejected,Pending');
    report.kycByMonth.forEach((m) => {
      rows.push(`${m.month},${m.submitted},${m.approved},${m.rejected},${m.submitted - m.approved - m.rejected}`);
    });
    rows.push('');
    rows.push('Customer Risk Distribution');
    rows.push('Risk Level,Count');
    report.customersByRisk.forEach((r) => {
      rows.push(`${r.level},${r.count}`);
    });
    rows.push('');
    rows.push('Document Upload Breakdown');
    rows.push('Document Type,Count');
    report.topDocumentTypes.forEach((d) => {
      rows.push(`${d.type},${d.count}`);
    });
    rows.push('');
    rows.push(`Average Processing Time,${report.avgProcessingTime}h`);

    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ekyc-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [report]);

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Reports & Analytics</h1>
          <p className="text-stone-500 text-sm mt-1">Export and analyze KYC performance data.</p>
        </div>
        <div className="flex gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
          <button
            onClick={exportCsv}
            disabled={!report || loading}
            className="bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-line" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Submitted', value: report?.kycByMonth.reduce((a, m) => a + m.submitted, 0) || 0, icon: 'ri-file-list-3-line', color: 'bg-sky-600' },
          { label: 'Approved', value: report?.kycByMonth.reduce((a, m) => a + m.approved, 0) || 0, icon: 'ri-check-double-line', color: 'bg-emerald-600' },
          { label: 'Rejected', value: report?.kycByMonth.reduce((a, m) => a + m.rejected, 0) || 0, icon: 'ri-close-circle-line', color: 'bg-red-500' },
          { label: 'Avg Processing', value: `${report?.avgProcessingTime || 0}h`, icon: 'ri-time-line', color: 'bg-brand-600' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-stone-100 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                <i className={`${stat.icon} text-white text-lg`} />
              </div>
              <span className="text-stone-500 text-sm">{stat.label}</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* KYC Trends */}
        <div className="bg-white rounded-xl border border-stone-100 p-6">
          <h3 className="font-bold text-stone-900 mb-6">KYC Trends</h3>
          {loading ? (
            <div className="h-64 animate-pulse bg-stone-50 rounded-lg" />
          ) : (
            <div className="flex flex-col gap-3">
              {report?.kycByMonth.map((m) => (
                <div key={m.month}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-stone-700">{m.month}</span>
                    <span className="text-stone-500 text-xs">{m.submitted} submitted</span>
                  </div>
                  <div className="h-6 bg-stone-100 rounded-full overflow-hidden flex">
                    <div
                      className="h-full bg-emerald-500"
                      style={{ width: `${(m.approved / maxSubmitted) * 100}%` }}
                    />
                    <div
                      className="h-full bg-red-400"
                      style={{ width: `${(m.rejected / maxSubmitted) * 100}%` }}
                    />
                    <div
                      className="h-full bg-sky-400"
                      style={{ width: `${((m.submitted - m.approved - m.rejected) / maxSubmitted) * 100}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-stone-500">
                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500" />{m.approved} approved</span>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-400" />{m.rejected} rejected</span>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-sky-400" />{m.submitted - m.approved - m.rejected} pending</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Risk Distribution */}
        <div className="bg-white rounded-xl border border-stone-100 p-6">
          <h3 className="font-bold text-stone-900 mb-6">Customer Risk Distribution</h3>
          {loading ? (
            <div className="h-64 animate-pulse bg-stone-50 rounded-lg" />
          ) : (
            <div className="flex flex-col gap-3">
              {report?.customersByRisk.map((r) => (
                <div key={r.level}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-stone-700 capitalize">{r.level} Risk</span>
                    <span className="text-stone-500 text-xs">{r.count} customers</span>
                  </div>
                  <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        r.level === 'low' ? 'bg-emerald-500' : r.level === 'medium' ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min((r.count / 100) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Document Types */}
      <div className="bg-white rounded-xl border border-stone-100 p-6">
        <h3 className="font-bold text-stone-900 mb-4">Document Upload Breakdown</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {report?.topDocumentTypes.map((d) => (
            <div key={d.type} className="text-center p-4 rounded-lg bg-stone-50">
              <p className="text-2xl font-bold text-stone-900">{d.count}</p>
              <p className="text-stone-500 text-xs capitalize mt-1">{d.type}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
